<script setup>

import { reactive, onMounted } from 'vue';

import { useAppStore } from 'store/store';
const { csrf, countries } = useAppStore();

import use$T from 'cfg/useTranslation';
const $t = use$T();

import errors       from 'cfg/errorBag';
import useRecaptcha from 'cfg/recaptcha';

import Auth        from '@Auth/Auth';
import MultiSelect from 'Common/MultiSelect/MultiSelect.vue';

const props = defineProps({
  recaptchaKey  : { type: String, default: '' },
  mondiadApiUrl : { type: String, default: '' }
});

const roles = [
  { value: 'advertiser' , title: 'roles.advertiser.title' , sub: 'roles.advertiser.sub' },
  { value: 'publisher'  , title: 'roles.publisher.title'  , sub: 'roles.publisher.sub' },
];

const userInfo = reactive({
  role    : 'advertiser',
  email   : '',
  country : '',
});

const state = reactive({
  submitting : false,
  sent       : false,
  sentEmail  : ''
});

onMounted(async ()=> {
  userInfo.country = await Auth.autoDetectCountry(props.mondiadApiUrl);
});

function readContext() {
  const qs = new URLSearchParams(window.location.search);
  const by = parseInt(qs.get('by'), 10);
  return {
    source       : qs.get('source') || qs.get('utm_source') || qs.get('utm') || undefined,
    referral     : qs.get('referral') || undefined,
    introducedBy : Number.isNaN(by) ? undefined : by,
    httpReferrer : document.referrer || undefined,
  };
}

async function google() {
  const { url } = await Auth.google({
    role    : userInfo.role,
    country : userInfo.country || undefined,
    csrf    : csrf['auth::user-register'],
  });

  window.location.href = url;
}

const getRecaptchaToken = useRecaptcha(props.recaptchaKey);

async function submit() {

  if (state.submitting) return;
  errors.clear();

  if (!userInfo.email.trim()) {
    return errors.add({ field: 'email', msg: $t('@errors.email_required') });
  }

  const ctx = readContext();

  state.submitting = true;
  const data = await Auth.magicLink({
    email        : userInfo.email.trim(),
    country      : userInfo.country || undefined,
    role         : userInfo.role,
    csrf         : csrf['auth::user-register'],

    recaptcha    : await getRecaptchaToken('registration_link'),
    source       : ctx.source,
    referral     : ctx.referral,
    introducedBy : ctx.introducedBy,
    httpReferrer : ctx.httpReferrer,
  }).finally(()=> state.submitting = false);

    state.sentEmail = data.email || userInfo.email.trim();
    state.sent = true;
}


</script>

<template>
  <div class="signup-card" :data-role="userInfo.role">

    <div class="signup-head">
      <h3>{{ $t('@title') }}</h3>
      <span class="micro">{{ $t('@duration') }}</span>
    </div>

    <template v-if="!state.sent">
      <div class="role-tabs">
        <div v-for="r in roles" :key="r.value"
         @click="userInfo.role = r.value"
         class="role-tab"
         :class="{ active: userInfo.role === r.value }"
         :data-role="r.value"
         >
           {{ $t(`@${r.title}`) }} <small>{{ $t(`@${r.sub}`) }}</small>
        </div>
      </div>

      <button class="btn-google" @click="google">
        <i class="fa-brands fa-google" aria-hidden="true" />
        <span>{{ $t('@google') }}</span>
      </button>

      <div class="form-divider"> {{ $t('@divider') }} </div>

      <form novalidate @submit.prevent="submit">
        <div v-if="errors.has('global')" class="form-error">{{ errors.first('global') }}</div>

        <div class="field">
          <label>{{ $t('@email_label') }}</label>
          <input v-model="userInfo.email" type="email" autocomplete="email" :placeholder="$t('@email_placeholder')" required>
          <small class="field-err">{{ errors.first('email') || '' }}</small>
        </div>

        <div class="field">
          <label>{{ $t('@country_label') }}</label>
          <MultiSelect
            search
            :options="countries"
            value="id"
            label="name"
            v-model="userInfo.country"
            :title="$t('@country_placeholder')"
          />
          <small class="field-err">{{ errors.first('countryId') || '' }}</small>
        </div>

        <vue-ladda class="btn btn-primary form-cta" data-style="expand-right" :loading="state.submitting">
          {{ $t(`@cta_${userInfo.role}`) }}
        </vue-ladda>

        <a :href="`/register?role=${encodeURIComponent(userInfo.role)}`" class="magic-link-toggle">{{ $t('@use_password') }}</a>
        <p class="form-foot" v-html="$t('@foot')"></p>
      </form>
    </template>

    <div v-if="state.sent" class="magic-link-success">
      <div class="magic-link-success-icon">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <h4>{{ $t('@success.title') }}</h4>
      <p><span v-html="$t('@success.body', { email: state.sentEmail })"></span></p>
    </div>
  </div>
</template>

<style lang="sass" scoped>

.signup-card
  background: var(--paper)
  border: 1px solid var(--line-2)
  border-radius: var(--r-lg)
  padding: 28px
  position: relative

.signup-head
  display: flex
  justify-content: space-between
  align-items: baseline
  margin-bottom: 18px

  h3
    font-size: 20px
    font-weight: 600

  .micro
    font-size: 12px
    color: var(--muted)

.role-tabs
  display: grid
  grid-template-columns: 1fr 1fr
  gap: 8px
  margin-bottom: 16px

.role-tab
  padding: 12px
  border-radius: var(--r-sm)
  border: 1px solid var(--line-2)
  background: var(--paper)
  text-align: center
  cursor: pointer
  font-size: 13px
  font-weight: 600
  color: var(--muted)
  transition: all .15s ease

  &.active
    border-color: var(--brand-400)
    background: var(--brand-50)
    color: var(--brand-700)

  small
    display: block
    font-weight: 400
    font-size: 11px
    margin-top: 2px
    opacity: .8

.field
  margin-bottom: 12px

  label
    display: block
    font-size: 12px
    font-weight: 600
    color: var(--ink-2)
    margin-bottom: 6px

  input, select
    width: 100%
    padding: 11px 14px
    font-family: inherit
    font-size: 14px
    border: 1px solid var(--line-2)
    border-radius: var(--r-sm)
    background: var(--paper)
    color: var(--ink)
    transition: border-color .15s ease, box-shadow .15s ease

  input:focus, select:focus
    outline: none
    border-color: var(--brand-400)
    box-shadow: 0 0 0 3px var(--brand-50)

.form-cta
  width: 100%
  justify-content: center
  margin-top: 8px
  padding: 14px
  font-size: 15px

  &[data-loading]
    opacity: .6
    cursor: not-allowed
    pointer-events: none

.form-foot
  font-size: 11px
  color: var(--muted)
  text-align: center
  margin-top: 12px
  line-height: 1.5

.form-error
  background: #fef2f2
  border: 1px solid #fecaca
  color: #b91c1c
  border-radius: 8px
  padding: 10px 12px
  font-size: 13px
  margin-bottom: 12px

.field-err
  display: block
  color: #dc2626
  font-size: 12px
  margin-top: 4px

  &:empty
    display: none

.btn-google
  width: 100%
  display: flex
  align-items: center
  justify-content: center
  gap: 10px
  padding: 12px 16px
  background: #fff
  color: #1F1F1F
  border: 1px solid var(--line-2)
  border-radius: var(--r-sm)
  font-family: inherit
  font-size: 14px
  font-weight: 600
  letter-spacing: -.01em
  cursor: pointer
  transition: background .15s ease, border-color .15s ease, box-shadow .15s ease
  margin-bottom: 16px

  &:hover
    background: #F8F9FA
    border-color: var(--ink-2)
    box-shadow: 0 1px 2px rgba(10, 22, 18, .06)

  svg
    flex-shrink: 0

  .fa-google
    flex-shrink: 0
    font-size: 16px
    color: #4285F4

.form-divider
  display: flex
  align-items: center
  gap: 12px
  margin: 4px 0 16px
  font-size: 11px
  font-weight: 600
  color: var(--muted)
  text-transform: uppercase
  letter-spacing: .1em

  &::before, &::after
    content: ""
    flex: 1
    height: 1px
    background: var(--line)

.magic-link-toggle
  display: block
  text-align: center
  margin-top: 12px
  font-size: 13px
  font-weight: 600
  color: var(--brand-600)

.magic-link-success
  text-align: center
  padding: 24px 16px
  background: var(--accent-50)
  border: 1px solid var(--accent-100)
  border-radius: var(--r-md)

.magic-link-success-icon
  width: 48px
  height: 48px
  margin: 0 auto 14px
  border-radius: 50%
  background: var(--brand-50)
  color: var(--brand-600)
  display: flex
  align-items: center
  justify-content: center

  h4
    font-size: 18px
    margin-bottom: 8px

.magic-link-success h4
  font-size: 18px
  margin-bottom: 8px

.magic-link-success p
  font-size: 14px
  color: var(--muted)
  line-height: 1.6

  :deep(strong)
    color: var(--ink)

.signup-card[data-role="publisher"]
  .form-cta
    background: var(--accent-400)
    &:hover
      background: var(--accent-500)

  .role-tab[data-role="publisher"].active
    border-color : var(--accent-400)
    background   : var(--accent-50)
    color        : var(--accent-600)

  .field input:focus, .field select:focus
    border-color: var(--accent-400)
    box-shadow: 0 0 0 3px var(--accent-50)
</style>
